import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, Space, message, notification } from 'antd';
import UserConfig from "@/config/user";
import { showDelConfirm, getOnOkFunction } from "@/lib/antdElementFunction";
import { getDetailsArray } from "@/lib/user";
import { dateFormat, convertTimestampToFormat } from "@/lib/dateTime";
import ResponsiveTable from '@/components/responsiveTable';
import { ReModal, ReButton } from "@/components/refineElement";
import { ModalProfileTeacher, ModalChangePassword } from "@/components/modal";
import { getUserAccountById } from "@/stores/user/action";
import { getLangList } from '@/lib/i18n';
import { hasPermission } from "@/lib/permissionUtil";
import { TeacherType } from './types';

const { updateFacultyAccount, changePassword, deleteUserAccount, resetUserPassword } = UserConfig;

type Props = {
  teacherArray: TeacherType[];
  fetchTeacherFunction: Function;
}

function TeacherManagement(props: Props) {
  const { teacherArray, fetchTeacherFunction } = props;
  const [teacherInfo, setTeacherInfo] = useState({} as TeacherType);
  const [isModalProfileVisible, setIsModalProfileVisible] = useState(false);
  const [isModalChangePasswordVisible, setIsModalChangePasswordVisible] = useState(false);
  const [handleType, setHandleType] = useState('');
  const dispatch: any = useDispatch();
  const { userInfo } = useSelector((state: any) => state.user);
  const { userAccountId } = teacherInfo;
  const studentListT: any = getLangList("studentList");
  const organizationT: any = getLangList("organization");
  const profileInfoT: any = getLangList("profileInfo");
  const errorT: any = getLangList("error");
  const buttonT: any = getLangList("button");
  const errorCodeT: any = getLangList("errorCode");
  const getUserAccountByIdFunction = (userAccountId: number) => () => {
    const params = { userAccountId };
    dispatch(getUserAccountById(params));
  }
  const getUserInfo = handleType === 'updateProfileInfo' ? getUserAccountByIdFunction(userAccountId) : () => { };
  const okUserFunction = {
    updateProfileInfo: updateFacultyInfoFunction,
  };

  const columns = [
    { title: studentListT.ACCOUNT, dataIndex: "username" },
    { title: organizationT.DEPARTMENT, dataIndex: "departmentName" },
    { title: organizationT.ROLE, dataIndex: "roleDesc" },
    { title: profileInfoT.NAME_CN, dataIndex: "nameCn" },
    { title: profileInfoT.NAME_EN, dataIndex: "nameEn" },
    {
      title: profileInfoT.DUE_DATE,
      dataIndex: "dueDate",
      width: 100,
      render: (text: number) => convertTimestampToFormat(text, dateFormat),
    },
    {
      title: studentListT.OPERATE,
      dataIndex: "action",
      render: (text: any, record: any) => (
        <Space>
          <ReButton iconName='search' onClick={clickUserEdit(record)}>
            {studentListT.DETAIL_INFO}
          </ReButton>

          {hasPermission('HR_MANAGEMENT_TEACHER_EDIT') &&
            <ReButton
              iconName='tool'
              onClick={clickPasswordEdit(record)}
              attributeObject={{ type: 'default' }}
            >
              {studentListT.CHANGE_PASSWORD}
            </ReButton>
          }

          {hasPermission('HR_MANAGEMENT_TEACHER_RESET_PASSWORD') &&
            <ReButton
              iconName='tool'
              onClick={clickResetUserPassword(record)}
              attributeObject={{ type: 'default' }}
            >
              {studentListT.RESET_PASSWORD}
            </ReButton>
          }

          {hasPermission('HR_MANAGEMENT_TEACHER_DELETE') &&
            <ReButton iconName='delete' onClick={clickUserDelete(record)} attributeObject={{ type: 'danger' }}>
              {buttonT.DELETE}
            </ReButton>
          }
        </Space>
      ),
    }
  ];

  const openNotification = (password: string) => {
    notification.success({
      message: `${studentListT.RESET_PASSWORD}${errorT.success}`,
      description: `${studentListT.TEMP_PASSWORD}: ${password}`,
      placement: 'top',
      duration: null,
    });
  };

  function toggleProfileModal(handleType: string = '') {
    setIsModalProfileVisible(!isModalProfileVisible);
    setHandleType(handleType);
  };

  const clickUserEdit = (data: TeacherType) => (e: any) => {
    const { userAccountId } = data;
    e.stopPropagation();
    setTeacherInfo(data);
    getUserAccountByIdFunction(userAccountId)();
    toggleProfileModal('updateProfileInfo');
  };

  const clickPasswordEdit = (data: TeacherType) => (e: any) => {
    e.stopPropagation();
    setTeacherInfo(data);
    setIsModalChangePasswordVisible(!isModalChangePasswordVisible);
  };

  const clickUserDelete = (data: TeacherType) => () => {
    showDelConfirm(() => deleteUserAccountFunction(data));
  };

  const clickResetUserPassword = (data: TeacherType) => () => {
    showDelConfirm(() => resetUserPasswordFunction(data), buttonT.CONFIRM_RESET_PASSWORD);
  };

  function updateFacultyInfoFunction(values: any) {
    const {
      username,
      password,
      nameCn,
      nameEn,
      departmentId,
      roleId,
      extension,
      cellPhoneNumber,
      businessEmail,
      personalEmail,
      accessCardNumber,
      agentDepartmentId,
      agentUserAccountId,
      dueDate,
      terminationDate,
      birthday,
      gender,
      identityNumber,
      idCardImg,
      qrCode,
    } = values;
    const detailsArray = getDetailsArray(values);
    const userInfoData = {
      ...userInfo,
      username,
      password,
      nameCn,
      nameEn,
      departmentId,
      roleId,
      extension,
      cellPhoneNumber,
      businessEmail,
      personalEmail,
      accessCardNumber,
      agentDepartmentId: agentDepartmentId ? agentDepartmentId : null,
      agentUserAccountId: agentUserAccountId ? agentUserAccountId : null,
      dueDate: dueDate?.format(dateFormat) ?? '',
      terminationDate: terminationDate?.format(dateFormat) ?? '',
      birthday: birthday?.format(dateFormat) ?? '',
      gender,
      identityNumber,
      details: detailsArray,
    };
    delete userInfoData.idCard;
    delete userInfoData.qrCode;
    const params = { stringFormat: { userInfo: userInfoData }, fileFormat: { idCardImg, qrCode } };

    if (!hasPermission('HR_MANAGEMENT_TEACHER_EDIT')) {
      return message.error(errorT.NO_PERMISSION);
    }

    updateFacultyAccount(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorCodeT[res.errorCode] ?? errorT.failed);
      }
      message.success(errorT.success);
      fetchTeacherFunction();
      toggleProfileModal();
    });
  }

  function updatePasswordFunction(data: any) {
    const { username } = teacherInfo;
    const { originPassword, newPassword, checkNewPassword } = data;
    const params = { originPassword, newPassword, checkNewPassword, username };

    changePassword(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      setIsModalChangePasswordVisible(!isModalChangePasswordVisible);
    });
  };

  function deleteUserAccountFunction(data: TeacherType) {
    const { userAccountId, username } = data;
    const params = { userAccountId, username };

    deleteUserAccount(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      fetchTeacherFunction();
    });
  };

  function resetUserPasswordFunction(data: TeacherType) {
    const { username } = data;
    const params = { username };

    resetUserPassword(params).then((res: any) => {
      const { success, value } = res;
      const { newPassword } = value;
      if (!success) {
        return message.error(errorT.failed);
      }
      openNotification(newPassword);
    });
  };

  return (
    <>
      <Row gutter={[0, 12]}>
        <Col span={24}>
          <ResponsiveTable
            dataSource={teacherArray}
            columns={columns}
          />
        </Col>
      </Row>

      {isModalProfileVisible &&
        <ReModal
          title={profileInfoT.PROFILE_INFO}
          onCancel={toggleProfileModal}
          visible={isModalProfileVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okUserFunction)}
          CollectedForm={ModalProfileTeacher}
          formProps={{
            getUserInfo,
            handleType,
          }}
          width={'700px'}
        />
      }

      {isModalChangePasswordVisible &&
        <ReModal
          title={profileInfoT.CHANGE_PASSWORD}
          onCancel={() => setIsModalChangePasswordVisible(!isModalChangePasswordVisible)}
          visible={isModalChangePasswordVisible}
          onOk={updatePasswordFunction}
          CollectedForm={ModalChangePassword}
        />
      }
    </>
  )
}

export default TeacherManagement;